import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { eventInfo } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Udfyld venligst alle felter');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Tak for din besked! Vi vender tilbage hurtigst muligt.');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Contact submit error:', err);
      toast.error('Noget gik galt. Prøv venligst igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-white" aria-labelledby="contact-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <header className="text-center mb-16">
          <span className="inline-block text-[#1a472a] font-semibold text-sm uppercase tracking-wider mb-4">
            Kontakt
          </span>
          <h2 id="contact-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Skriv til os
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Har du spørgsmål, vil du være frivillig, eller ønsker du at støtte? Vi hører gerne fra dig.
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="bg-[#faf7f2] rounded-3xl p-8 sm:p-12">
            <form onSubmit={handleSubmit} className="space-y-6" aria-label="Kontaktformular" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Dit navn
                </label>
                <Input
                  id="contact-name"
                  type="text"
                  data-testid="contact-name-input"
                  placeholder="Indtast dit navn"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-14 rounded-xl border-gray-200 focus:border-[#1a472a] focus:ring-[#1a472a]"
                  required
                  autoComplete="name"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Din email
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  placeholder="Indtast din email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-14 rounded-xl border-gray-200 focus:border-[#1a472a] focus:ring-[#1a472a]"
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                  Din besked
                </label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  placeholder="Skriv din besked her..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="min-h-[150px] rounded-xl border-gray-200 focus:border-[#1a472a] focus:ring-[#1a472a] resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="w-full bg-[#1a472a] hover:bg-[#2d5a3d] text-white h-14 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60"
              >
                <Send className="w-5 h-5 mr-2" aria-hidden="true" />
                {submitting ? 'Sender...' : 'Send besked'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <aside className="flex flex-col justify-center" aria-label="Kontaktoplysninger">
            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#1a472a]/10 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <Mail className="w-6 h-6 text-[#1a472a]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Email</h3>
                  <a
                    href={`mailto:${eventInfo.email}`}
                    className="text-[#1a472a] hover:underline text-lg"
                    aria-label={`Send email til ${eventInfo.email}`}
                  >
                    {eventInfo.email}
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#1a472a]/10 rounded-xl flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <MapPin className="w-6 h-6 text-[#1a472a]" />
                </div>
                <address className="not-italic">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Festens lokation</h3>
                  <p className="text-gray-600 text-lg">
                    {eventInfo.location}<br />
                    {eventInfo.address}
                  </p>
                </address>
              </div>

              {/* Partner info */}
              <div className="bg-[#faf7f2] rounded-2xl p-6 border border-gray-100">
                <p className="text-gray-600 mb-2 text-sm uppercase tracking-wider font-semibold">
                  Tilmelding til festen
                </p>
                <p className="text-gray-700">
                  Tilmelding til juletræsfesten sker via <strong>BROEN Vejle</strong>, som står for at udvælge familierne.
                </p>
                <a
                  href="https://broen-danmark.dk/vejle/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#1a472a] font-semibold mt-3 hover:underline"
                  aria-label="Besøg BROEN Vejles hjemmeside (åbner i nyt vindue)"
                >
                  Besøg BROEN Vejle
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
